import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Play,
    Edit,
    Trash2,
    Mail,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2,
    FileBarChart,
    Calendar
} from 'lucide-react';
import {
    claudeQueryService,
    DigestDTO
} from '../../../../../services/claudeQueryService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface DigestDashboardProps {
    onEditDigest: (digest: DigestDTO) => void;
    onCreateDigest: () => void;
}

export const DigestDashboard: React.FC<DigestDashboardProps> = ({
    onEditDigest,
    onCreateDigest
}) => {
    const { t } = useTranslation();
    const [digests, setDigests] = useState<DigestDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [runningDigests, setRunningDigests] = useState<Set<number>>(new Set());

    const loadDigests = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await claudeQueryService.getDigests();
            setDigests(data);
        } catch (err: any) {
            setError(err.message || t('manager.modules.claudeQuery.digests.loadError', {}, 'Kunne ikke laste sammendrag'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadDigests();
    }, [loadDigests]);

    const handleRun = async (digest: DigestDTO) => {
        try {
            setRunningDigests(prev => new Set(prev).add(digest.digestId));
            const result = await claudeQueryService.executeDigest(digest.digestId);

            // Oppdater digest-kortet
            setDigests(prev => prev.map(d => {
                if (d.digestId === digest.digestId) {
                    return {
                        ...d,
                        lastRunAt: result.executedAt,
                        lastRunStatus: result.success ? 'completed' : 'failed'
                    };
                }
                return d;
            }));

            if (!result.success) {
                setError(t('manager.modules.claudeQuery.digests.sendFailedFor', { '0': digest.name, '1': result.message }, `Sending feilet for "${digest.name}": ${result.message}`));
            }
        } catch (err: any) {
            setError(t('manager.modules.claudeQuery.digests.runError', { '0': err.message }, `Feil ved kjøring: ${err.message}`));
        } finally {
            setRunningDigests(prev => {
                const next = new Set(prev);
                next.delete(digest.digestId);
                return next;
            });
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('manager.modules.claudeQuery.digests.deleteConfirm', {}, 'Er du sikker på at du vil slette dette sammendraget?'))) return;

        try {
            await claudeQueryService.deleteDigest(id);
            setDigests(prev => prev.filter(d => d.digestId !== id));
        } catch (err: any) {
            setError(t('manager.modules.claudeQuery.digests.deleteError', { '0': err.message }, `Feil ved sletting: ${err.message}`));
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return d.toLocaleDateString('nb-NO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status?: string) => {
        switch (status) {
            case 'completed':
                return (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle size={12} />
                        {t('manager.modules.claudeQuery.digests.statusSent', {}, 'Sendt')}
                    </span>
                );
            case 'failed':
            case 'email_failed':
                return (
                    <span className="flex items-center gap-1 text-xs text-red-600">
                        <XCircle size={12} />
                        {t('manager.modules.claudeQuery.digests.statusFailed', {}, 'Feilet')}
                    </span>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-blue-500" size={32} />
                <span className="ml-3 text-gray-500">{t('manager.modules.claudeQuery.digests.loading', {}, 'Laster sammendrag...')}</span>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">
                    {t('manager.modules.claudeQuery.digests.subtitle', {}, 'Sett sammen flere datakilder til én komplett e-postrapport')}
                </p>
                <button
                    onClick={onCreateDigest}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm
                             hover:bg-blue-600 font-medium transition-colors"
                >
                    <Plus size={14} />
                    {t('manager.modules.claudeQuery.digests.newDigest', {}, 'Nytt sammendrag')}
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-sm text-red-700">
                    <AlertCircle size={16} />
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                        &times;
                    </button>
                </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-auto">
                {digests.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        <FileBarChart size={48} className="mb-3 text-gray-300" />
                        <p className="text-lg font-medium text-gray-500">{t('manager.modules.claudeQuery.digests.emptyTitle', {}, 'Ingen sammendrag ennå')}</p>
                        <p className="text-sm mt-1">
                            {t('manager.modules.claudeQuery.digests.emptyDescription', {}, 'Lag ditt første sammendrag for å få automatiske rapporter på e-post.')}
                        </p>
                        <button
                            onClick={onCreateDigest}
                            className="mt-4 flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                        >
                            <Plus size={14} />
                            {t('manager.modules.claudeQuery.digests.createDigest', {}, 'Opprett sammendrag')}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {digests.map(digest => (
                            <div
                                key={digest.digestId}
                                className="bg-white rounded-lg border border-gray-200 hover:border-blue-300
                                         hover:shadow-md transition-all p-4"
                            >
                                <div className="flex items-start justify-between">
                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FileBarChart size={16} className="text-blue-500 flex-shrink-0" />
                                            <h3 className="font-semibold text-gray-800 truncate">
                                                {digest.name}
                                            </h3>
                                            {digest.scheduleEnabled && (
                                                <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5
                                                             rounded-full flex items-center gap-1 flex-shrink-0">
                                                    <Calendar size={10} />
                                                    {t('manager.modules.claudeQuery.common.scheduled', {}, 'Schedulert')}
                                                </span>
                                            )}
                                        </div>

                                        {digest.description && (
                                            <p className="text-sm text-gray-500 mb-2 line-clamp-1">
                                                {digest.description}
                                            </p>
                                        )}

                                        <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                                            <span className="flex items-center gap-1">
                                                <Mail size={11} />
                                                {digest.deliveryEmail}
                                            </span>
                                            <span>
                                                {digest.sections.length === 1
                                                    ? t('manager.modules.claudeQuery.digests.sectionCountSingular', { '0': digest.sections.length }, `${digest.sections.length} seksjon`)
                                                    : t('manager.modules.claudeQuery.digests.sectionCountPlural', { '0': digest.sections.length }, `${digest.sections.length} seksjoner`)}
                                            </span>
                                            {digest.lastRunAt && (
                                                <span className="flex items-center gap-1">
                                                    <Clock size={11} />
                                                    {t('manager.modules.claudeQuery.digests.lastRun', { '0': formatDate(digest.lastRunAt) ?? '' }, `Siste: ${formatDate(digest.lastRunAt)}`)}
                                                    {getStatusBadge(digest.lastRunStatus)}
                                                </span>
                                            )}
                                            {digest.nextRunAt && (
                                                <span className="text-blue-400">
                                                    {t('manager.modules.claudeQuery.digests.nextRun', { '0': formatDate(digest.nextRunAt) ?? '' }, `Neste: ${formatDate(digest.nextRunAt)}`)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                                        <button
                                            onClick={() => handleRun(digest)}
                                            disabled={runningDigests.has(digest.digestId)}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-md
                                                     hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed
                                                     text-xs font-medium transition-colors"
                                        >
                                            {runningDigests.has(digest.digestId) ? (
                                                <Loader2 size={12} className="animate-spin" />
                                            ) : (
                                                <Play size={12} />
                                            )}
                                            {runningDigests.has(digest.digestId) ? t('manager.modules.claudeQuery.digests.sending', {}, 'Sender...') : t('manager.modules.claudeQuery.digests.sendNow', {}, 'Send nå')}
                                        </button>
                                        <button
                                            onClick={() => onEditDigest(digest)}
                                            className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50
                                                     rounded transition-colors"
                                            title={t('common.buttons.edit', {}, 'Rediger')}
                                        >
                                            <Edit size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(digest.digestId)}
                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50
                                                     rounded transition-colors"
                                            title={t('common.buttons.delete', {}, 'Slett')}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
